import type React from "react";
import {Fragment, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useAppDispatch, useAppSelector} from "../store/hooks";
import {fetchSentEmailsByTenant} from "../store/sentEmailsSlice";
import {fetchTenants, tenantsSelectors} from "../store/tenantsSlice";
import type {SentEmail} from "../types/sentEmail";
import type {TenantSummaryResponse} from "../types/tenant";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import Snackbar from "@mui/material/Snackbar";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import {PageHeader} from "../components/ui/PageHeader";
import {MonoText} from "../components/ui/StatusChip";
import {colors, fonts} from "../theme/tokens";
import {formatDateTime} from "../util/format";

type SortColumn = "status" | "toEmailAddress" | "subject" | "sentAt";
type SortDirection = "asc" | "desc";

function formatTemplateData(value: string | undefined): string {
    if (value == null || value === "") return "";
    try {
        const parsed = JSON.parse(value);
        return typeof parsed === "object" ? JSON.stringify(parsed, null, 2) : value;
    } catch {
        return value;
    }
}

export function SentEmailsPage() {
    const {t} = useTranslation();
    const dispatch = useAppDispatch();
    const tenants = useAppSelector(tenantsSelectors.selectAll);
    const tenantsStatus = useAppSelector((s) => s.tenants.status);
    const list = useAppSelector((s) => s.sentEmails.list);
    const listTenantId = useAppSelector((s) => s.sentEmails.currentTenantId);
    const status = useAppSelector((s) => s.sentEmails.status);
    const error = useAppSelector((s) => s.sentEmails.error);

    const [selectedTenant, setSelectedTenant] = useState<TenantSummaryResponse | null>(null);
    const [search, setSearch] = useState("");
    const [failedOnly, setFailedOnly] = useState(false);
    const [sortColumn, setSortColumn] = useState<SortColumn>("sentAt");
    const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [snackMessage, setSnackMessage] = useState<string | null>(null);

    useEffect(() => {
        if (tenantsStatus !== "idle") return;
        void dispatch(fetchTenants());
    }, [dispatch, tenantsStatus]);

    useEffect(() => {
        if (error) setSnackMessage(error);
    }, [error]);

    // Only the selected tenant's list is shown; anything else in the store belongs to a previous selection.
    const emails = useMemo(() => (selectedTenant && listTenantId === selectedTenant.id ? list : []), [list, listTenantId, selectedTenant]);
    const failedCount = emails.filter((email) => email.status === "FAILURE").length;

    const filteredAndSorted = useMemo(() => {
        const query = search.trim().toLowerCase();
        const items = emails.filter(
                (email) =>
                        (!failedOnly || email.status === "FAILURE") &&
                        (!query || email.toEmailAddress.toLowerCase().includes(query) || (email.subject?.toLowerCase().includes(query) ?? false)),
        );
        const compare = (a: SentEmail, b: SentEmail): number => {
            switch (sortColumn) {
                case "status":
                    return a.status.localeCompare(b.status);
                case "toEmailAddress":
                    return a.toEmailAddress.localeCompare(b.toEmailAddress);
                case "subject":
                    return (a.subject ?? "").localeCompare(b.subject ?? "");
                case "sentAt":
                    return new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime();
            }
        };
        const direction = sortDirection === "asc" ? 1 : -1;
        return [...items].sort((a, b) => direction * compare(a, b));
    }, [emails, search, failedOnly, sortColumn, sortDirection]);

    const handleSort = (column: SortColumn) => {
        if (sortColumn === column) {
            setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortColumn(column);
            setSortDirection("asc");
        }
    };

    const handleTenantChange = (_: unknown, value: TenantSummaryResponse | null) => {
        setSelectedTenant(value);
        setExpandedId(null);
        setFailedOnly(false);
        if (value) void dispatch(fetchSentEmailsByTenant(value.id));
    };

    const toggleExpanded = (email: SentEmail) => setExpandedId((id) => (id === email.id ? null : email.id));

    const sortLabels: Record<SortColumn, string> = {
        status: t("SENT_EMAILS_COLUMN_STATUS"),
        toEmailAddress: t("SENT_EMAILS_COLUMN_RECIPIENT"),
        subject: t("SENT_EMAILS_COLUMN_SUBJECT"),
        sentAt: t("SENT_EMAILS_COLUMN_SENT_AT"),
    };

    const sortHeader = (column: SortColumn, sx?: React.ComponentProps<typeof TableCell>["sx"]) => (
            <TableCell sortDirection={sortColumn === column ? sortDirection : false} sx={sx}>
                <TableSortLabel active={sortColumn === column} direction={sortColumn === column ? sortDirection : "asc"} onClick={() => handleSort(column)}>
                    {sortLabels[column]}
                </TableSortLabel>
            </TableCell>
    );

    const loading = status === "loading" && selectedTenant !== null;

    return (
            <>
                <PageHeader title={t("NAV_SENT_EMAILS")} subtitle={t("SENT_EMAILS_SUBTITLE")} />

                <Box sx={{display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5}}>
                    <Autocomplete
                            value={selectedTenant}
                            onChange={handleTenantChange}
                            options={tenants}
                            getOptionLabel={(tenant) => tenant.name}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            renderOption={(props, tenant) => {
                                const {key, ...optionProps} = props;
                                return (
                                        <Box component="li" key={key} {...optionProps} sx={{gap: 1}}>
                                            <span>{tenant.name}</span>
                                            <MonoText muted>{tenant.id}</MonoText>
                                        </Box>
                                );
                            }}
                            renderInput={(params) => (
                                    <TextField
                                            {...params}
                                            placeholder={t("SENT_EMAILS_SELECT_TENANT")}
                                            size="small"
                                            inputProps={{...params.inputProps, "aria-label": t("SENT_EMAILS_SELECT_TENANT")}}
                                    />
                            )}
                            sx={{width: 320}}
                    />
                    <TextField
                            placeholder={t("SENT_EMAILS_SEARCH_PLACEHOLDER")}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            size="small"
                            sx={{width: 320}}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{fontSize: 18, color: colors.faint}} />
                                            </InputAdornment>
                                    ),
                                },
                                htmlInput: {"aria-label": t("SENT_EMAILS_SEARCH_PLACEHOLDER")},
                            }}
                    />
                    {selectedTenant && (
                            <ButtonBase
                                    onClick={() => setFailedOnly((value) => !value)}
                                    aria-pressed={failedOnly}
                                    sx={{
                                        height: 28,
                                        px: 1.25,
                                        gap: 0.75,
                                        borderRadius: "14px",
                                        fontSize: "12.5px",
                                        border: `1px solid ${failedOnly ? colors.error : colors.errorPanelBorder}`,
                                        backgroundColor: failedOnly ? colors.errorTint : colors.errorPanelBackground,
                                        color: colors.errorPanelText,
                                        fontWeight: failedOnly ? 500 : 400,
                                    }}
                            >
                                <Box aria-hidden sx={{width: 6, height: 6, borderRadius: "50%", backgroundColor: colors.error}} />
                                {t("SENT_EMAILS_FAILED_ONLY", {count: failedCount})}
                            </ButtonBase>
                    )}
                    <Box sx={{flex: 1}} />
                    <Typography variant="body2" sx={{color: colors.faint}}>
                        {t("TENANTS_SORTED_BY", {column: sortLabels[sortColumn]})} {sortDirection === "asc" ? "↑" : "↓"}
                    </Typography>
                </Box>

                <Paper variant="outlined" sx={{position: "relative", overflow: "hidden"}}>
                    {loading && (
                            <Box sx={{position: "absolute", inset: 0, bgcolor: "rgba(255,255,255,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10}}>
                                <CircularProgress />
                            </Box>
                    )}
                    <TableContainer>
                        <Table aria-label={t("SENT_EMAILS_TABLE_ARIA")}>
                            <TableHead>
                                <TableRow>
                                    {sortHeader("status", {width: 56})}
                                    {sortHeader("toEmailAddress")}
                                    {sortHeader("subject")}
                                    {sortHeader("sentAt", {width: 180})}
                                    <TableCell sx={{width: 56}}>
                                        <Box component="span" sx={{position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)"}}>{t("SENT_EMAILS_EXPAND_ROW")}</Box>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {!selectedTenant ? (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center" sx={{py: 5}}>
                                                <Typography sx={{color: colors.muted}}>{t("SENT_EMAILS_SELECT_TENANT_HINT")}</Typography>
                                            </TableCell>
                                        </TableRow>
                                ) : filteredAndSorted.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center" sx={{py: 5}}>
                                                <Typography sx={{color: colors.muted}}>
                                                    {status === "succeeded" ? (emails.length === 0 ? t("SENT_EMAILS_EMPTY") : t("SENT_EMAILS_NO_MATCH")) : ""}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                ) : (
                                        filteredAndSorted.map((email) => {
                                            const expanded = expandedId === email.id;
                                            return (
                                                    <Fragment key={email.id}>
                                                        <TableRow
                                                                hover
                                                                sx={{cursor: "pointer", "& > td": expanded ? {borderBottom: "none", backgroundColor: colors.rowHover} : undefined}}
                                                                onClick={() => toggleExpanded(email)}
                                                        >
                                                            <TableCell><StatusIcon status={email.status} /></TableCell>
                                                            <TableCell sx={{color: colors.ink}}>{email.toEmailAddress}</TableCell>
                                                            <TableCell>{email.subject ?? "-"}</TableCell>
                                                            <TableCell sx={{whiteSpace: "nowrap"}}>{formatDateTime(email.sentAt)}</TableCell>
                                                            <TableCell sx={{py: 0}} onClick={(e) => e.stopPropagation()}>
                                                                <IconButton
                                                                        size="small"
                                                                        aria-expanded={expanded}
                                                                        aria-label={expanded ? t("SENT_EMAILS_COLLAPSE_ROW") : t("SENT_EMAILS_EXPAND_ROW")}
                                                                        onClick={() => toggleExpanded(email)}
                                                                >
                                                                    {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                                                </IconButton>
                                                            </TableCell>
                                                        </TableRow>
                                                        {expanded && (
                                                                <TableRow>
                                                                    <TableCell colSpan={5} sx={{backgroundColor: colors.rowHover, pt: 0, pb: 2}}>
                                                                        <EmailDetails email={email} />
                                                                    </TableCell>
                                                                </TableRow>
                                                        )}
                                                    </Fragment>
                                            );
                                        })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                <Snackbar open={snackMessage != null} autoHideDuration={6000} onClose={() => setSnackMessage(null)} anchorOrigin={{vertical: "bottom", horizontal: "center"}}>
                    <Alert severity="error" variant="filled" onClose={() => setSnackMessage(null)}>
                        {snackMessage}
                    </Alert>
                </Snackbar>
            </>
    );
}

function StatusIcon({status}: {status: SentEmail["status"]}) {
    const {t} = useTranslation();
    if (status === "SUCCESS") return <CheckCircleIcon sx={{fontSize: 18, color: colors.success, display: "block"}} aria-label={t("SENT_EMAILS_STATUS_SUCCESS")} />;
    if (status === "FAILURE") return <ErrorIcon sx={{fontSize: 18, color: colors.error, display: "block"}} aria-label={t("SENT_EMAILS_STATUS_FAILURE")} />;
    return <BlockIcon sx={{fontSize: 18, color: colors.faint, display: "block"}} aria-label={t("SENT_EMAILS_STATUS_DISABLED")} />;
}

const DETAIL_LABEL_SX = {fontSize: "12.5px", color: colors.muted} as const;
const DETAIL_VALUE_SX = {m: 0, fontSize: "12.5px", color: colors.ink, wordBreak: "break-word"} as const;

function EmailDetails({email}: {email: SentEmail}) {
    const {t} = useTranslation();
    const ids: {labelKey: string; value: string | undefined}[] = [
        {labelKey: "SENT_EMAILS_DETAIL_APPOINTMENT", value: email.appointmentId},
        {labelKey: "SENT_EMAILS_DETAIL_CUSTOMER", value: email.customerId},
        {labelKey: "SENT_EMAILS_DETAIL_EMPLOYEE", value: email.employeeId},
    ];
    const templateData = formatTemplateData(email.templateData);
    return (
            <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", lg: "minmax(0, 1.4fr) minmax(0, 1fr)"}, gap: 3, pl: {lg: 7}}}>
                <Box>
                    <Typography variant="overline" component="h3" sx={{color: colors.faint, display: "block", mb: 0.5}}>{t("SENT_EMAILS_MESSAGE")}</Typography>
                    {email.htmlBody ? (
                            <SandboxedHtml html={email.htmlBody} title={email.subject ?? t("SENT_EMAILS_MESSAGE")} />
                    ) : (
                            <Typography variant="body2" sx={{color: colors.muted}}>{t("SENT_EMAILS_NO_BODY")}</Typography>
                    )}
                </Box>
                <Box>
                    <Typography variant="overline" component="h3" sx={{color: colors.faint, display: "block", mb: 0.5}}>{t("SENT_EMAILS_DETAILS")}</Typography>
                    <Box component="dl" sx={{display: "grid", gridTemplateColumns: "max-content 1fr", columnGap: 3, rowGap: 0.75, m: 0}}>
                        <Box component="dt" sx={DETAIL_LABEL_SX}>{t("SENT_EMAILS_DETAIL_SENDER")}</Box>
                        <Box component="dd" sx={DETAIL_VALUE_SX}>{email.fromEmailAddress}</Box>
                        {email.templateName && (
                                <>
                                    <Box component="dt" sx={DETAIL_LABEL_SX}>{t("SENT_EMAILS_DETAIL_TEMPLATE")}</Box>
                                    <Box component="dd" sx={{...DETAIL_VALUE_SX, fontFamily: fonts.mono}}>{email.templateName}</Box>
                                </>
                        )}
                        {ids.filter((id) => id.value).map((id) => (
                                <Fragment key={id.labelKey}>
                                    <Box component="dt" sx={DETAIL_LABEL_SX}>{t(id.labelKey)}</Box>
                                    <Box component="dd" sx={{...DETAIL_VALUE_SX, fontFamily: fonts.mono}}>{id.value}</Box>
                                </Fragment>
                        ))}
                    </Box>
                    {templateData && (
                            <>
                                <Typography sx={{...DETAIL_LABEL_SX, mt: 1.5, mb: 0.5}}>{t("SENT_EMAILS_DETAIL_TEMPLATE_DATA")}</Typography>
                                <Box
                                        component="pre"
                                        sx={{m: 0, p: 1.25, borderRadius: "4px", backgroundColor: "#fff", border: `1px solid ${colors.cardBorder}`, fontFamily: fonts.mono, fontSize: "11.5px", whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 240, overflow: "auto"}}
                                >
                                    {templateData}
                                </Box>
                            </>
                    )}
                    {email.errorMessage && (
                            <Box
                                    role="alert"
                                    sx={{mt: 1.5, display: "flex", gap: 1, alignItems: "flex-start", p: 1.25, borderRadius: "4px", border: `1px solid ${colors.errorPanelBorder}`, backgroundColor: colors.errorPanelBackground, color: colors.errorPanelText, fontSize: "12.5px"}}
                            >
                                <ErrorOutlineIcon sx={{fontSize: 18}} />
                                <Box component="span" sx={{wordBreak: "break-word"}}>{email.errorMessage}</Box>
                            </Box>
                    )}
                </Box>
            </Box>
    );
}

/**
 * Renders a sent message without trusting it: the sandbox blocks scripts, forms and top-level navigation,
 * while same-origin access (scripts still disabled) lets the frame grow to the message's full height.
 */
function SandboxedHtml({html, title}: {html: string; title: string}) {
    const [height, setHeight] = useState(160);
    const document = `<!doctype html><html><head><base target="_blank"><style>body{margin:0;padding:16px;font:13px/1.5 Roboto,system-ui,sans-serif;color:#16202e}img{max-width:100%}</style></head><body>${html}</body></html>`;
    return (
            <Box
                    component="iframe"
                    title={title}
                    sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                    srcDoc={document}
                    onLoad={(event: React.SyntheticEvent<HTMLIFrameElement>) => {
                        const body = event.currentTarget.contentDocument?.body;
                        if (body) setHeight(Math.max(80, body.scrollHeight));
                    }}
                    sx={{display: "block", width: "100%", height, border: `1px solid ${colors.cardBorder}`, borderRadius: "4px", backgroundColor: "#fff"}}
            />
    );
}
